import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  ResponsiveContainer, ComposedChart, Area, Bar,
  BarChart, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ReferenceLine,
} from 'recharts';
import './ScmPage.css';

const API_BASE = process.env.REACT_APP_API_URL;

async function fetchPurchaseOrders(getToken) {
  const token = await getToken();
  const r = await fetch(`${API_BASE}/tripletex/purchase-orders`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) throw new Error(`Failed to fetch purchase orders (${r.status})`);
  return r.json();
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function deliveryUrgency(dateStr) {
  if (!dateStr) return 'unknown';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  const diffDays = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0)  return 'overdue';
  if (diffDays <= 7) return 'soon';
  return 'ok';
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatAmount(amount, currency) {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(amount) + ' ' + (currency || '');
}

function totalUnits(orderLines) {
  return (orderLines || []).reduce((s, l) => s + (l.quantity || 0), 0);
}

function monthLabel(monthKey) {
  const [y, m] = monthKey.split('-');
  return new Date(+y, +m - 1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

// ── Sub-components ────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cls = status === 'STATUS_CONFIRMED' ? 'scm-badge scm-badge--confirmed'
            : status === 'STATUS_SENT'      ? 'scm-badge scm-badge--sent'
            :                                 'scm-badge';
  const label = status === 'STATUS_CONFIRMED' ? 'Bekreftet'
              : status === 'STATUS_SENT'      ? 'Sendt'
              :                                 status;
  return <span className={cls}>{label}</span>;
}

function DeliveryDate({ dateStr }) {
  const urgency = deliveryUrgency(dateStr);
  return (
    <span className={`scm-delivery scm-delivery--${urgency}`}>
      {urgency === 'overdue' && <span className="scm-delivery-icon">⚠</span>}
      {formatDate(dateStr)}
    </span>
  );
}

function SourceBadge({ source }) {
  return (
    <span className={`scm-source-badge scm-source-badge--${source}`}>
      {source === 'mock' ? '◈ MOCK DATA' : '● LIVE'}
    </span>
  );
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="scm-stat-card">
      <span className="scm-stat-label">{label}</span>
      <strong className="scm-stat-value" style={accent ? { color: accent } : {}}>{value}</strong>
      {sub && <span className="scm-stat-sub">{sub}</span>}
    </div>
  );
}

function FulfilmentBar({ pct }) {
  const color = pct >= 100 ? '#46BB89' : pct > 0 ? '#63B3ED' : 'rgba(255,255,255,0.12)';
  return (
    <div className="scm-fulfilment-bar-wrap" title={`${pct}% produced`}>
      <div className="scm-fulfilment-bar" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
    </div>
  );
}

function OrderLineTable({ lines }) {
  const hasProduction = lines.some(l => l.produced != null);
  return (
    <table className="scm-lines-table">
      <thead>
        <tr>
          <th>Product number</th>
          <th>Description</th>
          <th className="scm-lines-qty">Ordered</th>
          {hasProduction && <th className="scm-lines-qty">Produced</th>}
          {hasProduction && <th className="scm-lines-qty">Remaining</th>}
        </tr>
      </thead>
      <tbody>
        {lines.map((line, i) => {
          const done = line.produced || 0;
          const rem  = line.remaining ?? Math.max(0, line.quantity - done);
          const pct  = line.quantity > 0 ? Math.round((done / line.quantity) * 100) : 0;
          return (
            <tr key={i}>
              <td className="scm-lines-pn">{line.productNumber}</td>
              <td>{line.productName}</td>
              <td className="scm-lines-qty">{line.quantity}</td>
              {hasProduction && (
                <td className="scm-lines-qty scm-lines-produced" style={{ color: done > 0 ? '#46BB89' : 'rgba(255,255,255,0.3)' }}>
                  {done > 0 ? done : '—'}
                  {done > 0 && <FulfilmentBar pct={pct} />}
                </td>
              )}
              {hasProduction && (
                <td className="scm-lines-qty" style={{ color: rem > 0 ? '#F6AD55' : '#46BB89' }}>
                  {rem > 0 ? rem : '✓'}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ProducedDevicesList({ devices }) {
  const [expanded, setExpanded] = useState(false);
  if (!devices?.length) return null;
  const show = expanded ? devices : devices.slice(0, 5);
  return (
    <div className="scm-produced-devices">
      <div className="scm-produced-head">
        <span>Produced devices ({devices.length})</span>
        {devices.length > 5 && (
          <button className="scm-produced-toggle" onClick={() => setExpanded(v => !v)}>
            {expanded ? 'Show less' : `Show all ${devices.length}`}
          </button>
        )}
      </div>
      <div className="scm-produced-list">
        {show.map((d, i) => (
          <div key={i} className="scm-produced-row">
            <span className="scm-produced-tag">{d.serviceTagNumber || d.serialNumber}</span>
            <span className="scm-produced-pn">{d.productNumber}</span>
            {d.scanDate && (
              <span className="scm-produced-date">{formatDate(d.scanDate)}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PoCard({ po }) {
  const [expanded, setExpanded] = useState(false);
  const urgency = deliveryUrgency(po.deliveryDate);

  return (
    <div className={`scm-po-card scm-po-card--${urgency}`}>
      <div className="scm-po-header" onClick={() => setExpanded(v => !v)}>
        <div className="scm-po-left">
          <span className="scm-po-number">PO {po.number}</span>
          <StatusBadge status={po.status} />
          {po.salesOrderNumber && (
            <span className="scm-po-order">Order {po.salesOrderNumber}</span>
          )}
        </div>

        <div className="scm-po-meta">
          <div className="scm-po-meta-item">
            <span className="scm-po-meta-label">Sent</span>
            <span>{formatDate(po.sentDate)}</span>
          </div>
          <div className="scm-po-meta-item">
            <span className="scm-po-meta-label">Delivery</span>
            <DeliveryDate dateStr={po.deliveryDate} />
          </div>
          <div className="scm-po-meta-item">
            <span className="scm-po-meta-label">Value</span>
            <span>{formatAmount(po.totalAmount, po.currency)}</span>
          </div>
          <div className="scm-po-meta-item scm-po-meta-item--units">
            <span className="scm-po-meta-label">Ordered</span>
            <span>{po.totalOrdered ?? totalUnits(po.orderLines)}</span>
          </div>
          {po.producedCount > 0 && (
            <div className="scm-po-meta-item">
              <span className="scm-po-meta-label">Produced</span>
              <span className="scm-produced-count">
                {po.producedCount}
                <span className="scm-produced-pct"> {po.fulfilmentPct}%</span>
              </span>
            </div>
          )}
        </div>

        <button className="scm-po-expand" aria-expanded={expanded}>
          {expanded ? '▲' : '▼'}
        </button>
      </div>

      {expanded && (
        <div className="scm-po-body">
          <div className="scm-po-contacts">
            {po.ourContact && (
              <span className="scm-contact"><span className="scm-contact-label">Our contact:</span> {po.ourContact}</span>
            )}
            {po.supplierContact && (
              <span className="scm-contact"><span className="scm-contact-label">Supplier contact:</span> {po.supplierContact}</span>
            )}
          </div>
          <OrderLineTable lines={po.orderLines} />
          <ProducedDevicesList devices={po.producedDevices} />
        </div>
      )}
    </div>
  );
}

// ── Chart tooltips ────────────────────────────────────────────────────────────
function TooltipBurnup({ active, payload, mode }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="scm-chart-tooltip">
      <div className="scm-tooltip-header">{d.label}</div>
      {mode === 'cumulative' ? (
        <>
          <div className="scm-tooltip-row">
            <span><span style={{ color: '#63B3ED' }}>■</span> Total ordered</span>
            <strong>{d.cumulativeOrdered}</strong>
          </div>
          <div className="scm-tooltip-row">
            <span><span style={{ color: '#46BB89' }}>■</span> Total produced</span>
            <strong>{d.cumulativeProduced}</strong>
          </div>
          <div className="scm-tooltip-divider" />
          <div className="scm-tooltip-row scm-tooltip-muted">
            <span>Backlog</span>
            <strong>{d.cumulativeOrdered - d.cumulativeProduced}</strong>
          </div>
        </>
      ) : (
        <>
          <div className="scm-tooltip-row">
            <span><span style={{ color: '#63B3ED' }}>■</span> Orders placed</span>
            <strong>{d.ordered}</strong>
          </div>
          <div className="scm-tooltip-row">
            <span><span style={{ color: '#46BB89' }}>■</span> Devices produced</span>
            <strong>{d.produced}</strong>
          </div>
        </>
      )}
    </div>
  );
}

function TooltipFulfilment({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const remColor = d.urgency === 'overdue' ? '#FC8181' : d.urgency === 'soon' ? '#F6AD55' : 'rgba(255,255,255,0.3)';
  return (
    <div className="scm-chart-tooltip">
      <div className="scm-tooltip-header">PO {d.name}</div>
      <div className="scm-tooltip-sub-line">Delivery: {formatDate(d.deliveryDate)}</div>
      <div className="scm-tooltip-divider" />
      <div className="scm-tooltip-row">
        <span><span style={{ color: '#46BB89' }}>■</span> Produced</span>
        <strong>{d.produced}</strong>
      </div>
      <div className="scm-tooltip-row">
        <span><span style={{ color: remColor }}>■</span> Remaining</span>
        <strong>{d.remaining}</strong>
      </div>
      <div className="scm-tooltip-row scm-tooltip-muted">
        <span>Total ordered</span>
        <strong>{d.ordered}</strong>
      </div>
      {d.ordered > 0 && (
        <div className="scm-tooltip-row scm-tooltip-muted">
          <span>Complete</span>
          <strong>{Math.round((d.produced / d.ordered) * 100)}%</strong>
        </div>
      )}
    </div>
  );
}

function TooltipBacklog({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="scm-chart-tooltip">
      <div className="scm-tooltip-header scm-tooltip-mono">{d.pn}</div>
      <div className="scm-tooltip-sub-line">{d.name}</div>
      <div className="scm-tooltip-divider" />
      <div className="scm-tooltip-row">
        <span><span style={{ color: '#46BB89' }}>■</span> Produced</span>
        <strong>{d.produced}</strong>
      </div>
      <div className="scm-tooltip-row">
        <span><span style={{ color: '#F6AD55' }}>■</span> Remaining</span>
        <strong>{d.remaining}</strong>
      </div>
      <div className="scm-tooltip-row scm-tooltip-muted">
        <span>Total ordered</span>
        <strong>{d.ordered}</strong>
      </div>
    </div>
  );
}

// ── Chart components ──────────────────────────────────────────────────────────
function BurnupChart({ data: chartData, mode }) {
  const today = new Date();
  const todayLabel = new Date(today.getFullYear(), today.getMonth())
    .toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });

  return (
    <ResponsiveContainer width="100%" height={340}>
      <ComposedChart data={chartData} margin={{ top: 10, right: 24, left: 0, bottom: 0 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="4 4" />
        <XAxis
          dataKey="label"
          tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }}
          tickLine={false}
          interval={2}
        />
        <YAxis tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }} tickLine={false} width={55} />
        <Tooltip content={props => <TooltipBurnup {...props} mode={mode} />} />
        <Legend
          wrapperStyle={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', paddingTop: '8px' }}
        />
        <ReferenceLine
          x={todayLabel}
          stroke="rgba(255,255,255,0.22)"
          strokeDasharray="4 3"
          label={{ value: 'Today', fill: 'rgba(255,255,255,0.35)', fontSize: 10, position: 'insideTopRight' }}
        />
        {mode === 'cumulative' ? (
          <>
            <Area
              type="monotone"
              dataKey="cumulativeOrdered"
              name="Total ordered"
              fill="rgba(99,179,237,0.08)"
              stroke="#63B3ED"
              strokeWidth={2}
              strokeDasharray="5 3"
              dot={false}
            />
            <Area
              type="monotone"
              dataKey="cumulativeProduced"
              name="Total produced"
              fill="rgba(70,187,137,0.18)"
              stroke="#46BB89"
              strokeWidth={2}
              dot={false}
            />
          </>
        ) : (
          <>
            <Bar dataKey="ordered" name="Orders placed" fill="rgba(99,179,237,0.65)" maxBarSize={14} radius={[3,3,0,0]} />
            <Bar dataKey="produced" name="Devices produced" fill="rgba(70,187,137,0.75)" maxBarSize={14} radius={[3,3,0,0]} />
          </>
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function FulfilmentChart({ data: chartData, mode, onPoClick, selectedPoNumber }) {
  const chartHeight = Math.max(300, Math.min(700, chartData.length * 26));
  const stacked = mode === 'stacked';
  const hasSelection = !!selectedPoNumber;

  const handleBarClick = (data) => {
    if (data?.name && onPoClick) onPoClick(data.name);
  };

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart
        data={chartData}
        layout="vertical"
        margin={{ top: 0, right: 24, left: 10, bottom: 0 }}
        barGap={3}
      >
        <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="4 4" horizontal={false} />
        <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }} tickLine={false} />
        <YAxis
          type="category"
          dataKey="name"
          width={70}
          tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
          tickLine={false}
        />
        <Tooltip content={<TooltipFulfilment />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
        <Bar
          dataKey="produced"
          name="Produced"
          stackId={stacked ? 'a' : undefined}
          maxBarSize={18}
          radius={stacked ? [0,0,0,0] : [0,3,3,0]}
          onClick={handleBarClick}
          style={{ cursor: 'pointer' }}
        >
          {chartData.map((entry, i) => (
            <Cell
              key={i}
              fill="#46BB89"
              opacity={hasSelection && entry.name !== selectedPoNumber ? 0.35 : 1}
            />
          ))}
        </Bar>
        <Bar
          dataKey="remaining"
          name="Remaining"
          stackId={stacked ? 'a' : undefined}
          maxBarSize={18}
          radius={[0,3,3,0]}
          onClick={handleBarClick}
          style={{ cursor: 'pointer' }}
        >
          {chartData.map((entry, i) => (
            <Cell
              key={i}
              fill={
                entry.urgency === 'overdue' ? 'rgba(252,129,129,0.7)' :
                entry.urgency === 'soon'    ? 'rgba(246,173,85,0.65)' :
                                              'rgba(255,255,255,0.13)'
              }
              opacity={hasSelection && entry.name !== selectedPoNumber ? 0.35 : 1}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function BacklogChart({ data: chartData, mode }) {
  const chartHeight = Math.max(250, Math.min(560, chartData.length * 34));
  const stacked = mode === 'stacked';
  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 24, left: 10, bottom: 0 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="4 4" horizontal={false} />
        <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }} tickLine={false} />
        <YAxis
          type="category"
          dataKey="pn"
          width={125}
          tick={{ fill: '#63B3ED', fontSize: 11, fontFamily: "'SF Mono','Fira Mono',monospace" }}
          tickLine={false}
        />
        <Tooltip content={<TooltipBacklog />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
        <Bar
          dataKey="produced"
          name="Produced"
          stackId={stacked ? 'a' : undefined}
          fill="rgba(70,187,137,0.75)"
          maxBarSize={22}
          radius={stacked ? [0,0,0,0] : [0,3,3,0]}
        />
        <Bar
          dataKey="remaining"
          name="Remaining"
          stackId={stacked ? 'a' : undefined}
          fill="rgba(246,173,85,0.65)"
          maxBarSize={22}
          radius={[0,3,3,0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── PO detail panel (click-to-inspect from Fulfilment chart) ─────────────────
function ScmPoPanel({ po, onClose }) {
  const ordered   = po.totalOrdered || totalUnits(po.orderLines);
  const produced  = po.producedCount || 0;
  const remaining = Math.max(0, ordered - produced);
  const pct       = ordered > 0 ? Math.round((produced / ordered) * 100) : 0;

  return (
    <div className="scm-po-panel">
      <div className="scm-po-panel-header">
        <div className="scm-po-panel-title">
          <span className="scm-po-number">PO {po.number}</span>
          <StatusBadge status={po.status} />
        </div>
        <button className="scm-po-panel-close" onClick={onClose} title="Close">✕</button>
      </div>

      <div className="scm-po-panel-meta">
        {po.salesOrderNumber && (
          <div className="scm-po-panel-row">
            <span className="scm-po-meta-label">Sales order</span>
            <span className="scm-po-order">Order {po.salesOrderNumber}</span>
          </div>
        )}
        <div className="scm-po-panel-row">
          <span className="scm-po-meta-label">Delivery</span>
          <DeliveryDate dateStr={po.deliveryDate} />
        </div>
        <div className="scm-po-panel-row">
          <span className="scm-po-meta-label">Value</span>
          <span>{formatAmount(po.totalAmount, po.currency)}</span>
        </div>
        {po.ourContact && (
          <div className="scm-po-panel-row">
            <span className="scm-po-meta-label">Our contact</span>
            <span>{po.ourContact}</span>
          </div>
        )}
        {po.supplierContact && (
          <div className="scm-po-panel-row">
            <span className="scm-po-meta-label">Supplier</span>
            <span>{po.supplierContact}</span>
          </div>
        )}
      </div>

      <div className="scm-po-panel-progress">
        <div className="scm-po-panel-progress-row">
          <span>{produced} <span style={{ color: 'rgba(255,255,255,0.35)' }}>/ {ordered} produced</span></span>
          <strong style={{ color: pct === 100 ? '#46BB89' : '#f5f7f4' }}>{pct}%</strong>
        </div>
        <div className="scm-po-panel-bar-wrap">
          <div className="scm-po-panel-bar" style={{ width: `${pct}%` }} />
        </div>
        {remaining > 0 && (
          <div className="scm-po-panel-remaining">{remaining} units remaining</div>
        )}
      </div>

      <div className="scm-po-panel-section-title">Order lines</div>
      <OrderLineTable lines={po.orderLines} />
      <ProducedDevicesList devices={po.producedDevices} />
    </div>
  );
}

// ── Customer sync (Tripletex PO delivery address → production records) ─────────
function CustomerSyncPanel({ getToken }) {
  const [open, setOpen] = React.useState(false);
  const [poNumber, setPoNumber] = React.useState('');
  const [verify, setVerify] = React.useState(null);
  const [backfill, setBackfill] = React.useState(null);
  const [busy, setBusy] = React.useState('');
  const [err, setErr] = React.useState('');

  const call = async (path, opts = {}) => {
    const token = await getToken();
    const r = await fetch(`${API_BASE}${path}`, { ...opts, headers: { Authorization: `Bearer ${token}`, ...(opts.headers || {}) } });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.message || data.detail || `Request failed (${r.status})`);
    return data;
  };

  const runVerify = async () => {
    const n = poNumber.trim();
    if (!n) return;
    setBusy('verify'); setErr(''); setVerify(null);
    try { setVerify(await call(`/tripletex/purchase-order/${encodeURIComponent(n)}`)); }
    catch (e) { setErr(e.message); } finally { setBusy(''); }
  };
  const runBackfill = async (dryRun) => {
    setBusy(dryRun ? 'dry' : 'run'); setErr(''); setBackfill(null);
    try { setBackfill(await call(`/tripletex/backfill-po-customers?limit=500${dryRun ? '&dryRun=1' : ''}`, { method: 'POST' })); }
    catch (e) { setErr(e.message); } finally { setBusy(''); }
  };

  const box = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '0.75rem', marginTop: '0.6rem', fontSize: '0.85rem', color: '#cbd5e1', whiteSpace: 'pre-wrap', wordBreak: 'break-word' };
  const btn = { padding: '0.45rem 0.9rem', borderRadius: 8, border: '1px solid #46bb89', background: 'rgba(70,187,137,0.15)', color: '#46bb89', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' };
  const ghost = { ...btn, border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: '#cbd5e1' };
  const input = { padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.04)', color: '#e5e7eb', minWidth: 160 };

  return (
    <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '0.85rem 1rem', margin: '0 0 1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <span style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8' }}>
          Customer sync — PO delivery address → production
        </span>
        <span style={{ color: '#94a3b8' }}>{open ? '▾' : '▸'}</span>
      </div>

      {open && (
        <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          {/* 1. Verify one PO */}
          <div>
            <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '0.35rem' }}>1. Verify one PO (check the mapping before backfilling)</div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input style={input} value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="PO number e.g. 84457" />
              <button style={btn} disabled={busy === 'verify'} onClick={runVerify}>{busy === 'verify' ? 'Checking…' : 'Verify PO'}</button>
            </div>
            {verify && <div style={box}>{JSON.stringify(verify, null, 2)}</div>}
          </div>

          {/* 2. Backfill */}
          <div>
            <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '0.35rem' }}>2. Backfill existing units (500 distinct POs per run — repeat until remaining = 0)</div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button style={ghost} disabled={!!busy} onClick={() => runBackfill(true)}>{busy === 'dry' ? 'Previewing…' : 'Preview (dry run)'}</button>
              <button style={btn} disabled={!!busy} onClick={() => runBackfill(false)}>{busy === 'run' ? 'Running…' : 'Run backfill'}</button>
            </div>
            {backfill && <div style={box}>{JSON.stringify(backfill, null, 2)}</div>}
          </div>

          {err && <div style={{ color: '#f87171', fontSize: '0.85rem' }}>{err}</div>}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ScmPage() {
  const { getToken } = useAuth();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sort, setSort]         = useState('delivery');

  // Graph view state
  const [view, setView]             = useState('list');       // 'list' | 'graph'
  const [graphTab, setGraphTab]     = useState('burnup');     // 'burnup' | 'fulfilment' | 'backlog'
  const [burnupMode, setBurnupMode] = useState('cumulative'); // 'cumulative' | 'monthly'
  const [barMode, setBarMode]       = useState('stacked');    // 'stacked' | 'grouped'
  const [selectedPO, setSelectedPO] = useState(null);
  const [dataScope, setDataScope]   = useState('all');        // 'all' | 'tracked'

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchPurchaseOrders(getToken)
      .then(res => { setData(res); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!data?.purchaseOrders) return [];
    const q = search.trim().toLowerCase();
    return data.purchaseOrders
      .filter(po => {
        const filterMap = { SENT: 'STATUS_SENT', CONFIRMED: 'STATUS_CONFIRMED' };
        if (statusFilter !== 'ALL' && po.status !== (filterMap[statusFilter] || statusFilter)) return false;
        if (!q) return true;
        return (
          po.number.includes(q) ||
          (po.salesOrderNumber || '').toLowerCase().includes(q) ||
          po.orderLines.some(l =>
            l.productNumber.toLowerCase().includes(q) ||
            l.productName.toLowerCase().includes(q)
          )
        );
      })
      .sort((a, b) => {
        if (sort === 'delivery') {
          const da = a.deliveryDate || '9999';
          const db = b.deliveryDate || '9999';
          return da < db ? -1 : da > db ? 1 : 0;
        }
        if (sort === 'po')    return Number(b.number) - Number(a.number);
        if (sort === 'value') return b.totalAmount - a.totalAmount;
        return 0;
      });
  }, [data, search, statusFilter, sort]);

  // Stats
  const stats = useMemo(() => {
    const pos = data?.purchaseOrders || [];
    const sent      = pos.filter(p => p.status === 'STATUS_SENT').length;
    const confirmed = pos.filter(p => p.status === 'STATUS_CONFIRMED').length;
    const overdue   = pos.filter(p => deliveryUrgency(p.deliveryDate) === 'overdue').length;
    const totalValue = pos.reduce((s, p) => s + (p.totalAmount || 0), 0);
    const totalUnitsAll = pos.reduce((s, p) => s + (p.totalOrdered || totalUnits(p.orderLines)), 0);
    const totalProduced = pos.reduce((s, p) => s + (p.producedCount || 0), 0);
    return { total: pos.length, sent, confirmed, overdue, totalValue, totalUnitsAll, totalProduced };
  }, [data]);

  // ── Graph data ───────────────────────────────────────────────────────────────

  // Set of product numbers that have ever been scanned in production
  const trackedSet = useMemo(() => {
    const s = new Set((data?.everProducedPns || []).filter(Boolean));
    // Also include product numbers from producedDevices in the returned POs
    (data?.purchaseOrders || []).forEach(po =>
      (po.producedDevices || []).forEach(d => { if (d.productNumber) s.add(d.productNumber); })
    );
    return s;
  }, [data]);

  const burnupData = useMemo(() => {
    if (!data?.purchaseOrders) return [];
    const pos = data.purchaseOrders;

    const allDates = [
      ...pos.map(p => p.sentDate),
      ...pos.map(p => p.deliveryDate),
    ].filter(Boolean).sort();
    if (!allDates.length) return [];

    // Monthly buckets from first sentDate to last deliveryDate
    const startD = new Date(allDates[0]);
    const endD   = new Date(allDates[allDates.length - 1]);
    const months = [];
    const cur = new Date(startD.getFullYear(), startD.getMonth(), 1);
    const end = new Date(endD.getFullYear(), endD.getMonth() + 1, 1);
    while (cur < end) {
      months.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`);
      cur.setMonth(cur.getMonth() + 1);
    }

    const isTracked = pn => dataScope === 'all' || trackedSet.has(pn);

    // Units ordered per month (keyed by sentDate month)
    const orderedByMonth = {};
    pos.forEach(po => {
      if (!po.sentDate) return;
      const key = po.sentDate.slice(0, 7);
      const qty = (po.orderLines || [])
        .filter(l => isTracked(l.productNumber))
        .reduce((s, l) => s + (l.quantity || 0), 0);
      if (qty > 0) orderedByMonth[key] = (orderedByMonth[key] || 0) + qty;
    });

    // Devices produced per month (keyed by scanDate month)
    const producedByMonth = {};
    pos.forEach(po => {
      (po.producedDevices || []).forEach(d => {
        if (!isTracked(d.productNumber)) return;
        const sd = d.scanDate || d.scandate;
        if (!sd) return;
        const key = String(sd).slice(0, 7);
        producedByMonth[key] = (producedByMonth[key] || 0) + 1;
      });
    });

    let cumOrdered = 0, cumProduced = 0;
    return months.map(key => {
      const mo = orderedByMonth[key] || 0;
      const mp = producedByMonth[key] || 0;
      cumOrdered  += mo;
      cumProduced += mp;
      return {
        key,
        label: monthLabel(key),
        ordered: mo,
        produced: mp,
        cumulativeOrdered: cumOrdered,
        cumulativeProduced: cumProduced,
      };
    });
  }, [data, dataScope, trackedSet]);

  const fulfilmentData = useMemo(() => {
    const isTracked = pn => dataScope === 'all' || trackedSet.has(pn);
    return [...filtered]
      .sort((a, b) => (a.deliveryDate || '9999') < (b.deliveryDate || '9999') ? -1 : 1)
      .slice(0, 50)
      .map(po => {
        const lines    = (po.orderLines || []).filter(l => isTracked(l.productNumber));
        const ordered  = lines.reduce((s, l) => s + (l.quantity || 0), 0);
        const produced = dataScope === 'tracked'
          ? (po.producedDevices || []).filter(d => trackedSet.has(d.productNumber)).length
          : (po.producedCount || 0);
        const remaining = Math.max(0, ordered - produced);
        return {
          name: po.number,
          ordered, produced, remaining,
          deliveryDate: po.deliveryDate,
          status: po.status,
          urgency: deliveryUrgency(po.deliveryDate),
        };
      })
      .filter(po => po.ordered > 0);
  }, [filtered, dataScope, trackedSet]);

  const backlogData = useMemo(() => {
    const isTracked = pn => dataScope === 'all' || trackedSet.has(pn);
    const byPn = {};
    filtered.forEach(po => {
      (po.orderLines || []).forEach(line => {
        const pn = line.productNumber;
        if (!pn || !isTracked(pn)) return;
        if (!byPn[pn]) byPn[pn] = { pn, name: line.productName || pn, ordered: 0, produced: 0 };
        byPn[pn].ordered  += line.quantity || 0;
        byPn[pn].produced += line.produced || 0;
      });
    });
    return Object.values(byPn)
      .map(p => ({ ...p, remaining: Math.max(0, p.ordered - p.produced) }))
      .sort((a, b) => b.remaining - a.remaining)
      .slice(0, 15);
  }, [filtered, dataScope, trackedSet]);

  const hasProducedData = stats.totalProduced > 0;

  const handlePoClick = useCallback((poNumber) => {
    const po = data?.purchaseOrders?.find(p => p.number === poNumber);
    if (!po) return;
    setSelectedPO(prev => prev?.number === poNumber ? null : po);
  }, [data]);

  const handleGraphTabChange = useCallback((tab) => {
    setGraphTab(tab);
    setSelectedPO(null);
  }, []);

  return (
    <div className="scm-page">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="scm-header">
        <div className="scm-header-left">
          <span className="scm-header-label">SCM</span>
          <h1 className="scm-header-title">Purchase Orders</h1>
          <span className="scm-header-sub">GPV Asia (Thailand) · Sendt &amp; Bekreftet</span>
        </div>
        <div className="scm-header-right">
          {data && <SourceBadge source={data.source} />}
          <div className="scm-view-toggle">
            <button
              className={`scm-view-btn${view === 'list' ? ' active' : ''}`}
              onClick={() => setView('list')}
            >
              ◉ List
            </button>
            <button
              className={`scm-view-btn${view === 'graph' ? ' active' : ''}`}
              onClick={() => setView('graph')}
            >
              ▲ Graph
            </button>
          </div>
          <button className="scm-refresh-btn" onClick={load} disabled={loading}>
            ↺ Refresh
          </button>
        </div>
      </div>

      {/* ── Customer sync (Tripletex PO → production) ──────────────────────── */}
      <CustomerSyncPanel getToken={getToken} />

      {/* ── Mock note ──────────────────────────────────────────────────────── */}
      {data?.source === 'mock' && (
        <div className="scm-mock-note">
          ◈ Displaying exported XLSX data. Live Tripletex API activates once consumer &amp; employee tokens are configured in the backend environment.
        </div>
      )}

      {/* ── Stats ──────────────────────────────────────────────────────────── */}
      {data && !loading && (
        <div className="scm-stats-row">
          <StatCard label="Total POs" value={stats.total} />
          <StatCard label="Sendt" value={stats.sent} accent="#F6AD55" />
          <StatCard label="Bekreftet" value={stats.confirmed} accent="#46BB89" />
          <StatCard label="Overdue" value={stats.overdue} accent={stats.overdue > 0 ? '#FC8181' : undefined} />
          <StatCard
            label="Units ordered"
            value={new Intl.NumberFormat('en-GB').format(stats.totalUnitsAll)}
          />
          {stats.totalProduced > 0 && (
            <StatCard
              label="Units produced"
              value={stats.totalProduced}
              accent="#46BB89"
              sub={`${Math.round((stats.totalProduced / stats.totalUnitsAll) * 100)}% complete`}
            />
          )}
          <StatCard
            label="Total value"
            value={new Intl.NumberFormat('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(stats.totalValue)}
            sub="EUR"
          />
        </div>
      )}

      {/* ── Loading / error ─────────────────────────────────────────────────── */}
      {loading && (
        <div className="scm-loading">
          <span className="scm-loading-spin">◈</span>
          Loading purchase orders…
        </div>
      )}
      {!loading && error && (
        <div className="scm-error">
          <strong>Failed to load:</strong> {error}
          <button className="scm-retry-btn" onClick={load}>Retry</button>
        </div>
      )}

      {/* ── List view ──────────────────────────────────────────────────────── */}
      {!loading && !error && view === 'list' && (
        <>
          <div className="scm-filters">
            <input
              className="scm-search"
              placeholder="Search PO number, sales order, or product…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div className="scm-filter-group">
              {['ALL', 'SENT', 'CONFIRMED'].map(s => (
                <button
                  key={s}
                  className={`scm-filter-btn${statusFilter === s ? ' active' : ''}`}
                  onClick={() => setStatusFilter(s)}
                >
                  {s === 'ALL' ? 'All' : s === 'SENT' ? 'Sendt' : 'Bekreftet'}
                </button>
              ))}
            </div>
            <div className="scm-sort-group">
              <span className="scm-sort-label">Sort:</span>
              {[
                { key: 'delivery', label: 'Delivery date' },
                { key: 'po',       label: 'PO number' },
                { key: 'value',    label: 'Value' },
              ].map(s => (
                <button
                  key={s.key}
                  className={`scm-sort-btn${sort === s.key ? ' active' : ''}`}
                  onClick={() => setSort(s.key)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 && (
            <div className="scm-empty">No purchase orders match your filters.</div>
          )}

          {filtered.length > 0 && (
            <div className="scm-po-list">
              <div className="scm-result-count">
                Showing {filtered.length} of {stats.total} purchase orders
              </div>
              {filtered.map(po => <PoCard key={po.id} po={po} />)}
            </div>
          )}
        </>
      )}

      {/* ── Graph view ─────────────────────────────────────────────────────── */}
      {!loading && !error && data && view === 'graph' && (
        <div className="scm-graph-section">

          {/* Tab row + mode controls */}
          <div className="scm-graph-tabs">
            <div className="scm-graph-tab-group">
              {[
                { key: 'burnup',     icon: '↗', label: 'Burn-up',    title: 'Cumulative orders vs production over time' },
                { key: 'fulfilment', icon: '▦', label: 'Fulfilment', title: 'Per-PO production progress sorted by delivery date' },
                { key: 'backlog',    icon: '◼', label: 'Backlog',    title: 'Top products by remaining units' },
              ].map(t => (
                <button
                  key={t.key}
                  className={`scm-graph-tab${graphTab === t.key ? ' active' : ''}`}
                  onClick={() => handleGraphTabChange(t.key)}
                  title={t.title}
                >
                  <span className="scm-tab-icon">{t.icon}</span> {t.label}
                </button>
              ))}
            </div>

            <div className="scm-graph-controls">
              {/* Scope toggle — always visible in graph view */}
              <div
                className="scm-scope-toggle"
                title="All items = everything ordered from supplier. Tracked only = products that have generated production scans and can do so again."
              >
                <button
                  className={`scm-scope-btn${dataScope === 'all' ? ' active' : ''}`}
                  onClick={() => setDataScope('all')}
                >
                  ◎ All items
                </button>
                <button
                  className={`scm-scope-btn scm-scope-btn--tracked${dataScope === 'tracked' ? ' active' : ''}`}
                  onClick={() => setDataScope('tracked')}
                  title={`${trackedSet.size} product numbers with production history`}
                >
                  ● Tracked only
                  {trackedSet.size > 0 && (
                    <span className="scm-scope-count">{trackedSet.size}</span>
                  )}
                </button>
              </div>

              {graphTab === 'burnup' && (
                <button
                  className={`scm-mode-btn${burnupMode === 'monthly' ? ' active' : ''}`}
                  onClick={() => setBurnupMode(v => v === 'cumulative' ? 'monthly' : 'cumulative')}
                  title="Switch between cumulative burn-up and monthly throughput view"
                >
                  {burnupMode === 'cumulative' ? '⊞ Monthly view' : '↗ Cumulative view'}
                </button>
              )}
              {(graphTab === 'fulfilment' || graphTab === 'backlog') && (
                <>
                  <div className="scm-filter-group scm-filter-group--inline">
                    {['ALL', 'SENT', 'CONFIRMED'].map(s => (
                      <button
                        key={s}
                        className={`scm-filter-btn${statusFilter === s ? ' active' : ''}`}
                        onClick={() => setStatusFilter(s)}
                      >
                        {s === 'ALL' ? 'All' : s === 'SENT' ? 'Sendt' : 'Bekreftet'}
                      </button>
                    ))}
                  </div>
                  <button
                    className={`scm-mode-btn${barMode === 'grouped' ? ' active' : ''}`}
                    onClick={() => setBarMode(v => v === 'stacked' ? 'grouped' : 'stacked')}
                    title="Switch between stacked (% of total) and grouped (side-by-side) bars"
                  >
                    {barMode === 'stacked' ? '⊞ Split bars' : '▦ Stack bars'}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Chart area */}
          <div className="scm-chart-wrap">

            {/* ── Burn-up ── */}
            {graphTab === 'burnup' && (
              <>
                <div className="scm-chart-title">
                  <div>
                    <span className="scm-chart-title-main">
                      {burnupMode === 'cumulative'
                        ? 'Cumulative production vs orders placed'
                        : 'Monthly throughput — orders placed vs devices produced'}
                    </span>
                    <span className="scm-chart-note">
                      {burnupMode === 'cumulative'
                        ? `${stats.total} POs · gap between lines = remaining backlog`
                        : `${stats.total} POs · bar height = units per month`}
                    </span>
                  </div>
                </div>
                {dataScope === 'all' ? (
                  <div className="scm-chart-info-note">
                    ◎ <strong>All items view</strong> — includes premounting procedures, accessories, and service kits that will never generate production scans. Switch to <strong>Tracked only</strong> to see the true production backlog for NFC-tracked devices.
                    {!hasProducedData && ' Produced line will rise once devices are scanned.'}
                  </div>
                ) : (
                  <div className="scm-chart-info-note scm-chart-info-note--tracked">
                    ● <strong>Tracked only</strong> — showing {trackedSet.size > 0 ? trackedSet.size + ' product numbers' : 'products'} with confirmed production history. These are items that have generated scan data at some point and can do so again. Ordered count reflects only these line items per PO.
                    {!hasProducedData && ' Produced line will rise once devices are scanned.'}
                  </div>
                )}
                <BurnupChart data={burnupData} mode={burnupMode} />
              </>
            )}

            {/* ── Fulfilment ── */}
            {graphTab === 'fulfilment' && (
              <>
                <div className="scm-chart-title">
                  <div>
                    <span className="scm-chart-title-main">PO fulfilment — sorted by delivery date</span>
                    <span className="scm-chart-note">
                      {fulfilmentData.length} POs shown · click a bar to inspect ·
                      <span style={{ color: '#46BB89' }}> ■ produced</span>
                      <span style={{ color: '#FC8181' }}> ■ overdue</span>
                      <span style={{ color: '#F6AD55' }}> ■ due soon</span>
                      <span style={{ color: 'rgba(255,255,255,0.3)' }}> ■ on track</span>
                    </span>
                  </div>
                </div>
                {fulfilmentData.length === 0 ? (
                  <div className="scm-empty">No POs match your filters.</div>
                ) : (
                  <div className={`scm-chart-and-panel${selectedPO ? ' scm-chart-and-panel--open' : ''}`}>
                    <div className="scm-chart-main">
                      <FulfilmentChart
                        data={fulfilmentData}
                        mode={barMode}
                        onPoClick={handlePoClick}
                        selectedPoNumber={selectedPO?.number}
                      />
                    </div>
                    {selectedPO && (
                      <ScmPoPanel po={selectedPO} onClose={() => setSelectedPO(null)} />
                    )}
                  </div>
                )}
              </>
            )}

            {/* ── Backlog ── */}
            {graphTab === 'backlog' && (
              <>
                <div className="scm-chart-title">
                  <div>
                    <span className="scm-chart-title-main">Top 15 products by remaining units</span>
                    <span className="scm-chart-note">
                      Aggregated across {filtered.length} POs ·
                      <span style={{ color: '#46BB89' }}> ■ produced</span>
                      <span style={{ color: '#F6AD55' }}> ■ remaining</span>
                    </span>
                  </div>
                </div>
                {backlogData.length === 0
                  ? <div className="scm-empty">No product data available.</div>
                  : <BacklogChart data={backlogData} mode={barMode} />
                }
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
